import React from 'react';
import { motion } from 'framer-motion';
import { Languages, Mic, Camera, Volume2, History, ArrowRightLeft } from 'lucide-react';

const TranslatorHero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-16">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
      <div className="absolute bottom-0 left-20 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div 
            className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-8"
            variants={itemVariants}
          >
            <Languages className="w-4 h-4 mr-2" />
            Powered by Google ML Kit
          </motion.div>

          {/* Main heading */}
          <motion.h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6"
            variants={itemVariants}
          >
            Break Language
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600">
              Barriers Instantly
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            Experience seamless multilingual communication with AI-powered translation, 
            voice input, camera OCR, and real-time speech synthesis in your pocket.
          </motion.p>

          {/* Feature icons */}
          <motion.div 
            className="flex flex-wrap justify-center items-center gap-8 mb-12"
            variants={itemVariants}
          >
            {[
              { icon: Languages, label: "8 Languages", description: "Smart Translation" },
              { icon: Mic, label: "Voice Input", description: "Speech to Text" },
              { icon: Camera, label: "Camera OCR", description: "Photo Translation" },
              { icon: Volume2, label: "Text-to-Speech", description: "Audio Output" },
              { icon: History, label: "Smart History", description: "Save & Search" },
              { icon: ArrowRightLeft, label: "Quick Swap", description: "Instant Switch" }
            ].map((feature, index) => (
              <div key={index} className="flex flex-col items-center group">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center mb-3 group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-900 text-sm">{feature.label}</p>
                  <p className="text-xs text-gray-500">{feature.description}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            className="flex justify-center items-center"
            variants={itemVariants}
          >
            <a
              href="https://play.google.com/store/apps/details?id=com.suuin.quicktranslator"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              Download on Google Play
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={itemVariants}
          >
            {[
              { number: "8+", label: "Languages Supported" },
              { number: "95%+", label: "Translation Accuracy" },
              { number: "10K+", label: "Happy Users" },
              { number: "24/7", label: "Offline Support" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Phone mockup */}
      <motion.div 
        className="relative max-w-sm mx-auto mt-16"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
        <div className="relative">
          <div className="w-64 h-128 mx-auto bg-black rounded-3xl p-2 shadow-2xl">
            <div className="w-full h-full bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex flex-col items-center justify-center">
              <Languages className="w-16 h-16 text-blue-600 mb-4" />
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">Quick Translator</div>
                <div className="text-sm text-gray-600">Ready to translate</div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default TranslatorHero; 